import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FileCheck, AlertTriangle } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import toast from 'react-hot-toast';

import api from '../api/axios';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { PersonName } from '../components/common/PersonColor';

// Admin: every sick report of a month with its sick note (AU) state. Admins
// can switch AU yes/no for any month; the change is audit-logged on the
// server and payroll / monthly report / time sheets read it live.

const berlinMonth = () => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Berlin' }).format(new Date()).slice(0, 7);
const fmtDate = (d) => format(parseISO(d), 'EEE, d MMM');
const fmtTime = (t) => (t ? t.slice(0, 5) : '');
const fmtStamp = (s) => (s ? format(parseISO(s.replace(' ', 'T') + 'Z'), 'd MMM yyyy, HH:mm') : '');
const fmtHours = (h) => `${Number(h).toFixed(2).replace(/\.?0+$/, '')} h`;

function AuSwitch({ record, onToggle, saving }) {
  const on = record.au_submitted;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={`AU for ${record.user_name} on ${record.date}`}
      onClick={() => onToggle(record)}
      disabled={saving}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '8px', minHeight: '36px', padding: '4px 10px 4px 4px',
        borderRadius: '999px', cursor: saving ? 'wait' : 'pointer', fontWeight: 700, fontSize: '0.8rem',
        border: `1px solid ${on ? 'var(--success)' : 'var(--warning, #E65100)'}`,
        background: on ? 'var(--success-light)' : '#FFF7ED',
        color: on ? 'var(--success)' : '#C2410C', whiteSpace: 'nowrap',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 34, height: 20, borderRadius: 10, position: 'relative', flexShrink: 0,
          background: on ? 'var(--success)' : '#CBD5E1', transition: 'background 0.15s',
        }}
      >
        <span style={{ position: 'absolute', top: 2, left: on ? 16 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.15s' }} />
      </span>
      {on ? 'AU yes' : 'AU no'}
    </button>
  );
}

function ChangedBy({ r }) {
  if (!r.au_changed_at) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  return (
    <span style={{ fontSize: '0.78rem', color: 'var(--text-light)' }}>
      {r.au_changed_by_name || 'Unknown'} · {fmtStamp(r.au_changed_at)}
    </span>
  );
}

export default function SickNotesPage() {
  const [params, setParams] = useSearchParams();
  const month = /^\d{4}-\d{2}$/.test(params.get('month') || '') ? params.get('month') : berlinMonth();
  const missingOnly = params.get('missing') === '1';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/sickness/month-overview?month=${month}`)
      .then((res) => setData(res.data))
      .catch((err) => { toast.error(err.response?.data?.error || 'Failed to load sick reports.'); setData(null); })
      .finally(() => setLoading(false));
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (r) => {
    setSavingId(r.id);
    try {
      await api.put(`/sickness/${r.id}/au`, { au: !r.au_submitted });
      toast.success(`${r.user_name}, ${fmtDate(r.date)}: ${r.au_submitted ? 'AU removed' : 'AU set'}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save.');
    } finally {
      setSavingId(null);
    }
  };

  const records = (data?.records || []).filter((r) => !missingOnly || !r.au_submitted);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Sick notes – month overview</h1>
          <p className="page-subtitle">Every sick report of the month with its sick note (AU). Changes apply to payroll and time sheets right away.</p>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '16px', padding: '12px 16px', display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.88rem', fontWeight: 600 }}>
          Month
          <input
            type="month"
            className="form-input"
            value={month}
            onChange={(e) => e.target.value && setParam('month', e.target.value)}
            style={{ width: 'auto', minHeight: '40px' }}
          />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.88rem', cursor: 'pointer', minHeight: '40px' }}>
          <input type="checkbox" checked={missingOnly} onChange={(e) => setParam('missing', e.target.checked ? '1' : '')} />
          Missing AU only
        </label>
        {data && (
          <span style={{ fontSize: '0.85rem', color: 'var(--text-light)', marginLeft: 'auto' }}>
            {data.total} sick {data.total === 1 ? 'report' : 'reports'}
            {data.missing_au > 0 && (
              <span className="badge badge-warning" style={{ marginLeft: '8px' }}>{data.missing_au} missing AU</span>
            )}
          </span>
        )}
      </div>

      {loading && !data ? (
        <LoadingSpinner label="Loading sick reports…" />
      ) : records.length === 0 ? (
        <div className="empty-state">
          <FileCheck className="empty-state-icon" />
          <h3>{missingOnly && data?.total ? 'No missing sick notes' : 'No sick reports'}</h3>
          <p>{missingOnly && data?.total ? 'Every sick report of this month has an AU.' : 'Nobody reported sick in this month.'}</p>
        </div>
      ) : (
        <>
          {/* Desktop: table */}
          <div className="card sick-notes-table" style={{ padding: 0, overflowX: 'auto' }}>
            <table style={{ fontSize: '0.88rem' }}>
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Date</th>
                  <th>Shift</th>
                  <th style={{ textAlign: 'right' }}>Hours counted</th>
                  <th>AU</th>
                  <th>Last changed by</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r) => (
                  <tr key={r.id}>
                    <td><PersonName userId={r.user_id} name={r.user_name} style={{ fontWeight: 600 }} /></td>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.date)}</td>
                    <td>{r.template_name} {fmtTime(r.start_time)}–{fmtTime(r.end_time)}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {r.au_submitted ? fmtHours(r.hours_counted) : (
                        <span style={{ color: 'var(--text-muted)' }} title={`${fmtHours(r.shift_hours)} shift, counted only with AU`}>0 h</span>
                      )}
                    </td>
                    <td><AuSwitch record={r} onToggle={toggle} saving={savingId === r.id} /></td>
                    <td><ChangedBy r={r} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Phone: cards */}
          <div className="sick-notes-cards" style={{ flexDirection: 'column', gap: '10px' }}>
            {records.map((r) => (
              <div key={r.id} className="card" style={{ padding: '12px 14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'flex-start' }}>
                  <div style={{ minWidth: 0 }}>
                    <PersonName userId={r.user_id} name={r.user_name} style={{ fontWeight: 700 }} />
                    <div style={{ fontSize: '0.84rem', color: 'var(--text-light)' }}>
                      {fmtDate(r.date)} · {r.template_name} {fmtTime(r.start_time)}–{fmtTime(r.end_time)}
                    </div>
                    <div style={{ fontSize: '0.84rem', marginTop: '2px' }}>
                      Hours counted: <strong>{r.au_submitted ? fmtHours(r.hours_counted) : '0 h'}</strong>
                      {!r.au_submitted && (
                        <span style={{ color: 'var(--text-muted)' }}> (shift {fmtHours(r.shift_hours)})</span>
                      )}
                    </div>
                  </div>
                  <AuSwitch record={r} onToggle={toggle} saving={savingId === r.id} />
                </div>
                <div style={{ marginTop: '6px' }}><ChangedBy r={r} /></div>
              </div>
            ))}
          </div>
        </>
      )}

      {data?.missing_au > 0 && !missingOnly && (
        <p style={{ fontSize: '0.8rem', color: 'var(--text-light)', marginTop: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <AlertTriangle size={13} /> Sick hours without AU are not counted in payroll (“Krankheitsstunden (mit AU)”).
        </p>
      )}
    </div>
  );
}
